import { t } from '@trimble/i18n';
import { AlignmentNavigationCapability } from '@trimble/web3d-capabilities';
import { createSignal, JSX, Show, createEffect, batch } from 'solid-js';
import { AlignmentNavigationStatus } from '@technology/web3d-plugin-trimbim';
import { CustomInput } from '@tcweb/connect-ui';
import {
  fixModusSliderStyle,
  UvIconButton,
} from '@trimble/ui-components/solidjs';
import { convertLength, getChainageUnit } from '@trimble/utilities';
import { LengthUnit } from '@fulcrum/lib';

const DEFAULT_NAVIGATION_SPEED = 30;
const DEFAULT_LENGTH_UNIT: LengthUnit = 'm';
const DEFAULT_LENGTH_DECIMALS = 2;

export const AlignmentNavigationDialog = (props: {
  alignmentNavigationCapability: AlignmentNavigationCapability | undefined;
  isOpen: boolean;
  lengthUnit?: LengthUnit;
  lengthDecimals?: number;
}): JSX.Element => {
  const [navigationSpeed, setNavigationSpeed] = createSignal(
    DEFAULT_NAVIGATION_SPEED
  );

  // Local signal for chainage input to prevent feedback loop during typing
  const [chainageInputValue, setChainageInputValue] = createSignal('');
  const [isChainageInputFocused, setIsChainageInputFocused] =
    createSignal(false);

  const getLengthUnit = (): LengthUnit =>
    getChainageUnit(props.lengthUnit ?? DEFAULT_LENGTH_UNIT);
  const getLengthDecimals = (): number =>
    props.lengthDecimals ?? DEFAULT_LENGTH_DECIMALS;

  // Initialize slider styles
  fixModusSliderStyle();

  // Sync navigation speed with capability
  createEffect(() => {
    if (props.alignmentNavigationCapability && props.isOpen) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AlignmentNavigationTool is not exported
      const tool = (props.alignmentNavigationCapability as any)
        .alignmentNavigationTool;
      if (tool && tool.autoPilotSpeed !== undefined) {
        setNavigationSpeed(tool.autoPilotSpeed);
      }
      // GetCurrent position from the tool
      // const point = tool.currentAlignmentPoint;
      // now would need to call alignment API to get chainage at this point
    }
  });

  // Sync chainage input value with capability's chainage when not focused
  // Capability's chainage is always in meters, convert to display unit
  createEffect(() => {
    const chainageMeters = props.alignmentNavigationCapability?.chainage();
    if (chainageMeters !== undefined && !isChainageInputFocused()) {
      const displayValue =
        convertLength(chainageMeters, 'm', getLengthUnit()) ?? chainageMeters;
      setChainageInputValue(displayValue.toFixed(getLengthDecimals()));
    }
  });

  // Handle navigation speed change
  const handleSpeedChange = (value: number): void => {
    setNavigationSpeed(value);
    if (props.alignmentNavigationCapability) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- AlignmentNavigationTool is not exported
      const tool = (props.alignmentNavigationCapability as any)
        .alignmentNavigationTool;
      if (tool) {
        tool.autoPilotSpeed = value;
      }
    }
  };

  const handleChainageJump = (value: string): void => {
    const chainageInDisplayUnit = parseFloat(value);
    if (
      isNaN(chainageInDisplayUnit) ||
      !props.alignmentNavigationCapability ||
      props.alignmentNavigationCapability.status() !==
        AlignmentNavigationStatus.Navigating
    ) {
      return;
    }

    // Convert from display unit to meters (capability uses meters internally)
    const chainageMeters =
      convertLength(chainageInDisplayUnit, getLengthUnit(), 'm') ??
      chainageInDisplayUnit;

    // Begin/end station from capability are already in meters
    const beginStation =
      props.alignmentNavigationCapability?.beginStation() ?? 0;
    const endStation = props.alignmentNavigationCapability?.endStation() ?? 1;

    let targetChainage: number;
    if (chainageMeters >= beginStation && chainageMeters <= endStation) {
      targetChainage = chainageMeters;
    } else if (chainageMeters < beginStation) {
      targetChainage = beginStation;
    } else {
      targetChainage = endStation;
    }

    void props.alignmentNavigationCapability?.navigateToPosition(
      targetChainage
    );
  };

  const handleChainageInputBlur = (): void => {
    batch(() => {
      setIsChainageInputFocused(false);
      // Format the value on blur to show proper decimals
      // Capability's chainage is in meters, convert to display unit
      const chainageMeters = props.alignmentNavigationCapability?.chainage();
      if (chainageMeters !== undefined) {
        const displayValue =
          convertLength(chainageMeters, 'm', getLengthUnit()) ?? chainageMeters;
        setChainageInputValue(displayValue.toFixed(getLengthDecimals()));
      }
    });
  };

  const handleChainageInputKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      // Blur the DOM input to submit the value. This triggers onBlur which calls
      // handleChainageJump and handleChainageInputBlur, ensuring the DOM focus state
      // matches the isChainageInputFocused signal and preventing a race condition
      // where continued typing after Enter gets overwritten by the chainage sync effect.
      (e.target as HTMLInputElement)?.blur();
    }
  };

  const handleDistanceSlide = (distance: number): void => {
    if (
      props.alignmentNavigationCapability &&
      props.alignmentNavigationCapability.status() ===
        AlignmentNavigationStatus.Navigating
    ) {
      void props.alignmentNavigationCapability?.navigateToPosition({
        distance,
      });
    }
  };

  return (
    <Show when={props.isOpen}>
      <div
        class="absolute top-full right-0 mt-1 bg-base-page border py-1 px-2 border-[var(--modecolor-modecontent-level2,#e0e1e9)] rounded-sm shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)] min-w-200"
        data-testid="alignment-navigation-dialog"
      >
        {/* Action Group - Toolbar buttons */}
        <div
          class={`flex items-center px-1 py-1 gap-3 rounded-sm bg-base-page min-h-[32px] min-w-[156px]`}
        >
          <UvIconButton
            dataTestId="align-snap-button"
            type="action"
            variant="outlined"
            tooltip={t('TC.3D.AlignmentNavigationToolbar.SnapButtonTooltip')}
            onClick={() =>
              void props.alignmentNavigationCapability?.snapToAlignment()
            }
            disabled={
              props.alignmentNavigationCapability?.status() !==
              AlignmentNavigationStatus.Navigating
            }
            icon="tc-icon-snap-line"
          />
          <UvIconButton
            dataTestId="auto-pilot-button"
            type="toggle"
            variant="outlined"
            tooltip={t('web3D.toolbar.actions.autoPilot')}
            onClick={() =>
              void props.alignmentNavigationCapability?.toggleAutoPilot()
            }
            pressed={props.alignmentNavigationCapability?.autoPilot() || false}
            disabled={
              props.alignmentNavigationCapability?.status() !==
              AlignmentNavigationStatus.Navigating
            }
            icon="play_circle"
          />
          <UvIconButton
            dataTestId="cursor-button"
            type="toggle"
            variant="outlined"
            tooltip={t('web3D.toolbar.actions.pickPath')}
            onClick={() =>
              void props.alignmentNavigationCapability?.togglePickingActive()
            }
            pressed={
              props.alignmentNavigationCapability?.pickingActive() || false
            }
            disabled={
              props.alignmentNavigationCapability?.status() ===
              AlignmentNavigationStatus.Inactive
            }
            icon="cursor"
            iconVariant={
              props.alignmentNavigationCapability?.pickingActive()
                ? 'solid'
                : 'outlined'
            }
          />
        </div>

        <Show
          when={
            props.alignmentNavigationCapability?.autoPilot() &&
            props.alignmentNavigationCapability?.status() ===
              AlignmentNavigationStatus.Navigating
          }
        >
          <div class="flex flex-col gap-2 mt-2">
            <div class="text-xs">
              {t('TC.3D.AlignmentNavigationDialog.NavigationSpeed')}
            </div>
            <div class="flex flex-col gap-2">
              <modus-wc-slider
                attr:value={navigationSpeed()}
                attr:min={1}
                attr:max={800}
                attr:step={1}
                on:inputChange={(e) => {
                  const speed = parseFloat(
                    (e.detail.target as HTMLInputElement).value
                  );
                  handleSpeedChange(speed);
                }}
              />
            </div>
          </div>
        </Show>

        <Show
          when={
            props.alignmentNavigationCapability?.status() ===
            AlignmentNavigationStatus.Navigating
          }
        >
          <div class="flex flex-col gap-2 mt-2">
            <div class="text-xs">
              {t('TC.3D.AlignmentNavigationDialog.PositionOnPath')}
            </div>
            <modus-wc-slider
              attr:value={
                props.alignmentNavigationCapability?.distance() ??
                Math.round(
                  props.alignmentNavigationCapability?.beginDistance() || 0
                )
              }
              attr:min={
                props.alignmentNavigationCapability?.beginDistance() || 0
              }
              attr:max={props.alignmentNavigationCapability?.endDistance() || 1}
              on:inputChange={(e) => {
                const distance = (e.detail.target as HTMLInputElement).value;
                handleDistanceSlide(parseFloat(distance));
              }}
            />

            <Show
              when={props.alignmentNavigationCapability?.isAlignment() === true}
            >
              <CustomInput
                name="chainage-input"
                label={t('TC.3D.AlignmentNavigationDialog.JumpToChainage')}
                type="number"
                placeholder=""
                value={chainageInputValue()}
                onInputRef={(ref) => ref && (ref.min = '0')}
                onInput={(
                  e: InputEvent & {
                    currentTarget: HTMLInputElement;
                    target: Element;
                  }
                ) => {
                  const value = e.currentTarget.value;
                  if (Number(value) < 0) {
                    e.currentTarget.value = chainageInputValue();
                    return;
                  }
                  setChainageInputValue(value);
                  // Native stepper (spinner buttons / arrow keys) reports an
                  // empty inputType; commit immediately so the camera moves
                  // without needing Enter or blur. Typed input still waits.
                  if (!e.inputType) {
                    handleChainageJump(value);
                  }
                }}
                onBlur={() => {
                  handleChainageJump(chainageInputValue());
                  handleChainageInputBlur();
                }}
                onFocus={() => setIsChainageInputFocused(true)}
                onKeyDown={(e) => handleChainageInputKeyDown(e)}
                disabled={
                  props.alignmentNavigationCapability?.status() !==
                  AlignmentNavigationStatus.Navigating
                }
                class="text-xs"
                data-cy="chainage-input"
              />
              <Show
                when={
                  props.alignmentNavigationCapability?.isInOverLength() === true
                }
              >
                <span class="text-xs mt-1 text-[var(--modus-wc-color-secondary)]">
                  {t('TC.3D.AlignmentNavigationDialog.InOverLength')}
                </span>
              </Show>
            </Show>
          </div>
        </Show>
      </div>
    </Show>
  );
};
